import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { billingApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { Button, Card, Spinner } from '../components/ui';
import { linkButtonClass } from '../utils/linkButtonClass';
import Layout from '../components/Layout';

export default function BillingSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'unconfirmed'>('loading');
  const [message, setMessage] = useState('');
  const [hoursAdded, setHoursAdded] = useState<number | null>(null);
  const [sessionsAdded, setSessionsAdded] = useState<number | null>(null);
  // ワンショットの決済が無制限プランのため記録のみで付与されなかった
  const [notGranted, setNotGranted] = useState(false);
  const [paymentType, setPaymentType] = useState<'oneshot' | 'subscription' | null>(null);
  const [planType, setPlanType] = useState<string | null>(null);

  // `refreshUser` は AuthProvider の再描画ごとに実体が変わるため、依存配列には入れず ref 経由で呼ぶ。
  // 依存に入れると「確認 → user 更新 → Provider 再描画 → 依存変化 → 再確認」で確認要求が止まらなくなる。
  const refreshUserRef = useRef(refreshUser);
  refreshUserRef.current = refreshUser;

  const confirmPayment = useCallback(async () => {
    if (!sessionId) {
      setStatus('success');
      setMessage('お支払いが完了しました');
      return;
    }

    setStatus('loading');
    try {
      const result = await billingApi.confirmSession(sessionId);
      setStatus('success');

      if (result.payment_type === 'subscription') {
        setPaymentType('subscription');
        setPlanType(result.plan_type);
        setMessage('プランの購入が完了しました');
      } else if (result.granted === false) {
        // status ではなく granted で判定する（Webhook が先に処理していると already_processed になる）
        setPaymentType('oneshot');
        setNotGranted(true);
        setMessage('お支払いが完了しました');
      } else if (result.hours_added > 0) {
        setPaymentType('oneshot');
        setHoursAdded(result.hours_added);
        setMessage(`${result.hours_added}時間が追加されました！`);
      } else if (result.sessions_added > 0) {
        setPaymentType('oneshot');
        setSessionsAdded(result.sessions_added);
        setMessage(`${result.sessions_added}回が追加されました！`);
      } else {
        setMessage('お支払いが完了しました');
      }

      // 反映後のプランを画面へ届ける。user の真実源は AuthContext（Query ではない）であり、
      // ここで再取得しないとダッシュボードもヘッダも旧プランのまま見える
      // （他のプラン変更系はリロードで解決しているが、この画面は Link のクライアント遷移のため）。
      await refreshUserRef.current();
    } catch (err) {
      console.error('Payment confirmation error:', err);
      // 確認が取れないまま「完了」と表示してはならない。決済だけ成立してプランが変わらない
      // 状態を利用者が成功と誤認し、申告されるまで表面化しない（実際に未反映が見逃された）。
      // 一方で Webhook 側の反映が遅れているだけの可能性もあるため失敗とも断定せず、
      // 確認できていない事実と再確認の導線を示す。
      setStatus('unconfirmed');
    }
  }, [sessionId]);

  useEffect(() => {
    confirmPayment();
  }, [confirmPayment]);

  if (status === 'loading') {
    return (
      <Layout>
        <div className="flex-1 flex items-center justify-center">
          <Card className="max-w-md w-full text-center">
            <div className="flex justify-center mb-6">
              <Spinner size="lg" />
            </div>
            <h1 className="text-2xl font-bold mb-4">処理中...</h1>
            <p className="text-gray-600">決済を確認しています</p>
          </Card>
        </div>
      </Layout>
    );
  }

  if (status === 'unconfirmed') {
    return (
      <Layout>
        <div className="flex-1 flex items-center justify-center">
          <Card className="max-w-md w-full text-center">
            <div className="flex justify-center mb-6">
              <AlertTriangle className="w-20 h-20 text-warning" />
            </div>

            <h1 className="text-2xl font-bold mb-4">反映を確認できませんでした</h1>

            <div className="bg-warning-bg border border-warning/30 rounded-lg p-4 mb-6 text-left">
              <p className="text-warning-fg text-sm">
                お支払いは完了していますが、プランへの反映を確認できませんでした。
                反映が遅れているだけの場合もありますので、少し待ってから「再確認する」をお試しください。
                解消しない場合はお問い合わせください（重ねてお支払いが発生することはありません）。
              </p>
            </div>

            <div className="space-y-3">
              <Button onClick={confirmPayment} className="w-full">
                再確認する
              </Button>
              <Link to="/billing" className={linkButtonClass('secondary', 'block w-full')}>
                プラン変更・課金ページへ
              </Link>
              <Link to="/contact" className={linkButtonClass('secondary', 'block w-full')}>
                お問い合わせ
              </Link>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex-1 flex items-center justify-center">
        <Card className="max-w-md w-full text-center">
          <div className="flex justify-center mb-6">
            <CheckCircle className="w-20 h-20 text-success" />
          </div>

          <h1 className="text-2xl font-bold mb-4">お支払い完了</h1>

          <p className="text-gray-600 mb-2">
            ご購入ありがとうございます！
          </p>

          {paymentType === 'oneshot' && hoursAdded !== null && hoursAdded > 0 && (
            <div className="bg-success-bg border border-success/30 rounded-lg p-4 mb-6">
              <p className="text-success-fg font-bold text-lg">
                +{hoursAdded}時間 追加されました
              </p>
            </div>
          )}

          {paymentType === 'oneshot' && sessionsAdded !== null && sessionsAdded > 0 && (
            <div className="bg-success-bg border border-success/30 rounded-lg p-4 mb-6">
              <p className="text-success-fg font-bold text-lg">
                +{sessionsAdded}回 追加されました
              </p>
            </div>
          )}

          {paymentType === 'oneshot' && notGranted && (
            <div className="bg-info-bg border border-info/30 rounded-lg p-4 mb-6">
              <p className="text-info-fg text-sm">
                無制限プランのため、時間・回数は追加されませんでした
              </p>
              {/* 特商法表記「返品の取扱条件」のただし書き（付与されなかった場合は返金）への導線 */}
              <p className="text-info-fg text-sm mt-2">
                ご購入分の返金をご希望の場合は、
                <Link to="/contact" className="underline underline-offset-2">
                  お問い合わせ
                </Link>
                ください。
              </p>
            </div>
          )}

          {paymentType === 'subscription' && planType && (
            <div className="bg-info-bg border border-info/30 rounded-lg p-4 mb-6">
              <p className="text-info-fg font-bold text-lg">
                {planType === 'lite' && 'ライトプラン'}
                {planType === 'standard' && 'スタンダードプラン'}
                {planType === 'unlimited' && '無制限プラン'}
                {!['lite', 'standard', 'unlimited'].includes(planType) && planType}
                に登録されました
              </p>
            </div>
          )}

          <p className="text-gray-600 mb-6">{message}</p>

          <div className="space-y-3">
            <Link to="/dashboard" className={linkButtonClass('primary', 'block w-full')}>
              ダッシュボードへ
            </Link>
            <Link to="/upload" className={linkButtonClass('secondary', 'block w-full')}>
              音声をアップロード
            </Link>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
